import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { Component, effect, inject, Signal, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '../../components/button/button.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectListComponent } from '../../components/select-list/select-list.component';
import { StepperComponent } from '../../components/stepper/stepper.component';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import TYPES from '../../constants/types';
import { AuthService, AUTH_SERVICE } from '../../services/auth.service';
import { DatabaseService, DATABASE_SERVICE } from '../../services/database.service';
import { LogService } from '../../services/log.service';
import { SelectListItem } from '../../types/select-list-item';
import { BodyReactionService } from '../../services/body-reaction.service';
import { BodyReaction } from '../../models/body-reaction';
import { UserBodyReaction } from '../../models/user-body-reaction';
import { Log } from '../../models/log';

@Component({
  selector: 'app-body-reaction',
  imports: [
    HeaderComponent,
    StepperComponent,
    ButtonComponent,
    TranslatePipe,
    SelectListComponent,
    MatSnackBarModule,
    DialogModule
  ],
  templateUrl: './body-reaction.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './body-reaction.component.scss'
})
export class BodyReactionComponent {
  BUTTON_TYPES:any;
  step: Signal<StepperComponent | undefined> = viewChild<StepperComponent>('stepper');
  listBodyReactions: WritableSignal<SelectListItem[]> = signal<SelectListItem[]>([]);

  bodyReactionDatabaseService: BodyReactionService<BodyReaction> = inject(BodyReactionService);
  userBodyReactionDatabaseService: DatabaseService<BodyReaction> = inject(DATABASE_SERVICE);
  authService: AuthService = inject(AUTH_SERVICE);

  snackBar:MatSnackBar = inject(MatSnackBar);
  dialog: Dialog = inject(Dialog);
  translate: TranslateService = inject(TranslateService);


  constructor(
    private router:Router,
    private logService: LogService<Log>,
  ){
    effect( ()=> {
      this.step()?.steps()[2].nativeElement.classList.add('active');
    });

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
  }

  async ngOnInit() {
    let customBodyReactions: BodyReaction[];
    if (!localStorage.getItem('custom-body-reactions')){
      customBodyReactions = await this.bodyReactionDatabaseService.getAllCustomBodyReactionsFromUser(this.authService.user()?.id || '');
      localStorage.setItem('custom-body-reactions', JSON.stringify(customBodyReactions));
    }else{
      customBodyReactions = JSON.parse(localStorage.getItem('custom-body-reactions') || '');
    }
    
    let systemBodyReactions: BodyReaction[];
    if (!localStorage.getItem('system-body-reactions')){
      systemBodyReactions = await this.bodyReactionDatabaseService.getAllSystemBodyReactions();
      localStorage.setItem('system-body-reactions', JSON.stringify(systemBodyReactions));
    }else{
      systemBodyReactions = JSON.parse(localStorage.getItem('system-body-reactions') || '');
    }

    const bodyReactions = systemBodyReactions.concat(customBodyReactions);
    const selectedBodyReactions: string[] | undefined = this.logService.log().body_reactions?.split(';');
      
    if(bodyReactions){
      for(let bodyReaction of bodyReactions){
        
        let selected = false;
        if (selectedBodyReactions?.filter((item) => item == bodyReaction.id).length){
          selected = true;
        }

        this.listBodyReactions.update(
          (current) => [
              ...current, {
              id: bodyReaction.id,  
              label: bodyReaction.label ? bodyReaction.label : '',
              selected: selected,
              provider: bodyReaction.provider ? bodyReaction.provider : ''
            }
          ]
        );
      }
    } 
  }

  close(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'log-close-dialog.alert_title',
        message: 'log-close-dialog.alert_message'
      },
    });

    dialogRef.closed.subscribe((result:any)  => {
      if(result?.action === 'continue'){
        this.logService.canAccessMindLogRegister.set(false);
        this.router.navigate(['/home'], { replaceUrl: true });  
      }
    });   
  }

  next(){
    const log = this.logService.log();
    let bodyReactions = ''; 
    for(const item of this.listBodyReactions()){
      if(item.id && item.selected === true){
        bodyReactions += item.id + ';'
      }
    }
    bodyReactions = bodyReactions.substring(0, bodyReactions.length-1);
    this.logService.log.update( 
      (current) => {
        return {...current, body_reactions: bodyReactions}
      }
    );
    
    // this.router.navigate(['/reason'], { replaceUrl: true });
    this.router.navigate(['/reason']);
    
  }

  back(){
    // this.router.navigate(['/felt'], { replaceUrl: true });    
    history.back();
  }

  async addNewBodyReaction(param:SelectListItem){
    const newBodyReaction: Partial<BodyReaction> = {
      label: param.label,
      provider: param.provider
    }

    let bodyReaction: BodyReaction = await this.bodyReactionDatabaseService.getByBodyReactionLabel(param.label);

    if(!bodyReaction){
      bodyReaction = await this.bodyReactionDatabaseService.create(
        TYPES.DATABASE.TABLES.BODY_REACTIONS, newBodyReaction
      );
    }

    const customBodyReactions: BodyReaction[] = JSON.parse(localStorage.getItem('custom-body-reactions') || '');
    
    const bodyReactionFound = customBodyReactions.find(
      (item) => {
        return item.label === param.label
      }
    )

    if(!bodyReactionFound){
      customBodyReactions.push(bodyReaction);
      localStorage.setItem('custom-body-reactions', JSON.stringify(customBodyReactions));

      const userBodyReaction: Partial<UserBodyReaction> = {
        user_id: this.authService.user()?.id,
        body_reaction_id: bodyReaction.id
      }

      this.userBodyReactionDatabaseService.create(TYPES.DATABASE.TABLES.USERS_BODY_REACTIONS, userBodyReaction);

      this.listBodyReactions.update(
        (current) => [
            ...current, {
            id: bodyReaction.id,  
            label: bodyReaction.label ? bodyReaction.label : '',
            selected: true,
            provider: bodyReaction.provider ? bodyReaction.provider : ''
          }
        ]
      );
      const message:string = this.translate.instant('info.body_reaction_added');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }else{
      const message:string = this.translate.instant('info.body_reaction_exists');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }

  }

  async deleteBodyReaction(param:SelectListItem){
    await this.bodyReactionDatabaseService.deleteBodyReactionFromUser(
      param.id || '',
      this.authService.user()?.id || '');
    
    const listUpdated = this.listBodyReactions().filter(
      (item) => {
        return item.id != param.id;
      }
    );

    this.listBodyReactions.set(listUpdated);

    const message:string = this.translate.instant('info.body_reaction_deleted');
    const action:string = this.translate.instant('buttons.dismiss');
    this.snackBar.open(message, action);

    const customBodyReactions: BodyReaction[] = JSON.parse(localStorage.getItem('custom-body-reactions') || '');
    const customBodyReactionsFiltered = customBodyReactions.filter(
      (item) => {
        return item.id != param.id
      }
    );
    localStorage.setItem('custom-body-reactions', JSON.stringify(customBodyReactionsFiltered));

  }

}